import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, Save, X, Plus, Trash2, Diamond } from "lucide-react";
import CollapsibleCard from "./CollapsibleCard";

const FEATURE_THEME = {
  hole:        { color: "#38BDF8", icon: "○", label: "Foro" },
  thread:      { color: "#F472B6", icon: "⌬", label: "Filettatura" },
  groove:      { color: "#A3E635", icon: "▭", label: "Cava" },
  pocket:      { color: "#FACC15", icon: "▣", label: "Tasca" },
  chamfer:     { color: "#94A3B8", icon: "◣", label: "Smusso" },
  fillet:      { color: "#94A3B8", icon: "◜", label: "Raccordo" },
  slot:        { color: "#22D3EE", icon: "▭", label: "Asola" },
  counterbore: { color: "#C084FC", icon: "⌽", label: "Lamatura" },
  countersink: { color: "#C084FC", icon: "▽", label: "Svasatura" },
};
const SEVERITY_COLORS = { warn: "#F59E0B", alert: "#EF4444", info: null };
const KIND_OPTIONS = Object.keys(FEATURE_THEME);
const SEVERITY_OPTIONS = ["info", "warn", "alert"];

/**
 * EditableDFMCard — viewer-tab card listing AI-detected feature_annotations
 * (holes, threads, grooves, …). Hover sync with the 2D image overlay (parent
 * passes ``hovered`` + ``setHovered``). In edit mode each row exposes kind /
 * label / quantity / severity inputs + delete; an "Add" button appends a
 * blank feature row whose bbox will be set later from the overlay click.
 */
export default function EditableDFMCard({ features, hovered, onHoverFeature, onSave }) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(features || []);
  const [saving, setSaving] = useState(false);
  if (!features || features.length === 0) return null;

  const startEdit = () => {
    setDraft(JSON.parse(JSON.stringify(features)));
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setDraft(features);
  };
  const updateF = (i, key, value) => {
    setDraft((curr) => curr.map((f, idx) => idx === i ? { ...f, [key]: value } : f));
  };
  const deleteF = (i) => setDraft((curr) => curr.filter((_, idx) => idx !== i));
  const addF = () => setDraft((curr) => [...curr, {
    kind: "hole",
    label: t("dfmCard.manualHole"),
    quantity: 1,
    severity: "info",
    bbox_normalized: [0.4, 0.4, 0.5, 0.5],
  }]);

  const submit = async () => {
    setSaving(true);
    const clean = draft
      .filter((f) => f.kind && (f.label || "").trim())
      .map((f) => ({
        ...f,
        quantity: parseInt(f.quantity, 10) || 1,
      }));
    try {
      await onSave({ feature_annotations: clean });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const rows = editing ? draft : features;
  const headerActions = !editing ? (
    <button
      type="button"
      onClick={startEdit}
      className="p-1 rounded text-slate-400 hover:text-pink-300 hover:bg-pink-500/10"
      title={t("viewerTab.editDfm")}
      data-testid="dfm-edit-btn"
    >
      <Pencil size={12}/>
    </button>
  ) : (
    <div className="flex items-center gap-1">
      <button type="button" onClick={cancel} className="p-1 rounded text-slate-400 hover:text-rose-300" data-testid="dfm-cancel-btn">
        <X size={12}/>
      </button>
      <button type="button" onClick={submit} disabled={saving} className="p-1 rounded text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50" data-testid="dfm-save-btn">
        <Save size={12}/>
      </button>
    </div>
  );

  return (
    <CollapsibleCard
      testid="dfm-features-card"
      className="border-pink-500/30"
      titleClassName="text-pink-300"
      storageKey="dfm"
      icon={<Diamond size={14}/>}
      title={t("dfmCard.title", { count: features.length })}
      actions={headerActions}
    >
      {editing && (
        <button
          type="button"
          onClick={addF}
          className="mb-2 text-[10px] flex items-center gap-1 px-2 py-0.5 rounded border border-pink-500/40 text-pink-300 hover:bg-pink-500/10"
          data-testid="dfm-add-feature-btn"
        >
          <Plus size={10}/> {t("dfmCard.addFeature")}
        </button>
      )}

      <ul className="space-y-1.5 text-[12px] font-mono-data">
        {rows.slice(0, 30).map((f, i) => {
          const theme = FEATURE_THEME[f.kind] || FEATURE_THEME.hole;
          const sevColor = SEVERITY_COLORS[f.severity] || theme.color;
          const isHovered = hovered === `feat-${i}`;
          if (editing) {
            return (
              <li key={`feat-edit-${i}`} className="space-y-1 px-2 py-1.5 rounded bg-slate-800/40 border border-slate-700/40">
                <div className="flex items-center gap-1.5">
                  <select
                    value={f.kind}
                    onChange={(e) => updateF(i, "kind", e.target.value)}
                    className="px-1 py-0.5 bg-slate-900 border border-slate-700 rounded text-xs"
                    data-testid={`dfm-kind-${i}`}
                  >
                    {KIND_OPTIONS.map((k) => <option key={k} value={k}>{t(`viewerTab.feat.${k}`)}</option>)}
                  </select>
                  <input
                    type="text"
                    value={f.label || ""}
                    onChange={(e) => updateF(i, "label", e.target.value)}
                    placeholder={t("dfmCard.labelPh")}
                    className="flex-1 px-1 py-0.5 bg-slate-900 border border-slate-700 rounded text-xs"
                    data-testid={`dfm-label-${i}`}
                  />
                  <button type="button" onClick={() => deleteF(i)} className="p-0.5 text-rose-400 hover:text-rose-300" data-testid={`dfm-del-${i}`}>
                    <Trash2 size={11}/>
                  </button>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-slate-400">
                  <span>{t("dfmCard.qty")}</span>
                  <input
                    type="number"
                    min="1"
                    value={f.quantity || 1}
                    onChange={(e) => updateF(i, "quantity", e.target.value)}
                    className="w-12 px-1 py-0.5 bg-slate-900 border border-slate-700 rounded text-xs"
                    data-testid={`dfm-qty-${i}`}
                  />
                  <span>{t("dfmCard.severity")}</span>
                  <select
                    value={f.severity || "info"}
                    onChange={(e) => updateF(i, "severity", e.target.value)}
                    className="px-1 py-0.5 bg-slate-900 border border-slate-700 rounded text-xs"
                    data-testid={`dfm-sev-${i}`}
                  >
                    {SEVERITY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </li>
            );
          }
          return (
            <li
              key={`feat-list-${i}`}
              onMouseEnter={() => onHoverFeature(`feat-${i}`)}
              onMouseLeave={() => onHoverFeature(null)}
              className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer transition-colors ${isHovered ? "bg-pink-500/10" : "hover:bg-slate-800/50"}`}
              data-testid={`feat-list-${f.kind}`}
            >
              <span style={{ color: sevColor || theme.color }} className="text-base leading-none">{theme.icon}</span>
              <span className="text-slate-200 flex-1 truncate">{f.label || t(`viewerTab.feat.${f.kind}`, { defaultValue: theme.label })}</span>
              {f.quantity > 1 && <span className="text-slate-400 text-[11px]">×{f.quantity}</span>}
              {f.severity && f.severity !== "info" && (
                <span
                  className="text-[10px] uppercase px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: `${SEVERITY_COLORS[f.severity]}22`, color: SEVERITY_COLORS[f.severity] }}
                >
                  {f.severity === "alert" ? t("dfmCard.critical") : t("dfmCard.warning")}
                </span>
              )}
            </li>
          );
        })}
        {!editing && features.length > 30 && (
          <li className="text-slate-500 text-center pt-1">{t("guardrailBadge.andMore", { count: features.length - 30 })}</li>
        )}
      </ul>
    </CollapsibleCard>
  );
}
